import { Combobox, cn } from '@atlas/ui-primitives'
import { useId } from 'react'
import { FieldShell } from '../field-shell/field-shell'

/** One option; `depth` indents it so a value tree reads as a hierarchy. */
export interface SelectFieldOption {
	value: string
	label: string
	depth?: number
}

export interface SelectFieldProps {
	/**
	 * Visible label. Pass `''` for a control whose column header already names it
	 * — `ariaLabel` then carries the accessible name.
	 */
	label: string
	/** Selected value code; '' renders the placeholder. */
	value: string
	options: SelectFieldOption[]
	onChange: (value: string) => void
	/** Placeholder for the empty selection, e.g. "all (untouched)". */
	placeholder?: string
	/**
	 * Drop the empty option entirely, for a field that always holds one of its
	 * values (an enum with a default). Offering "unset" there would be an invalid
	 * choice the form has to reject afterwards.
	 */
	isClearable?: boolean
	/**
	 * Accessible name, when the visible `label` alone is ambiguous — a table that
	 * repeats the same field per row leaves a screen-reader user hearing "Brand"
	 * a dozen times with no way to tell which row each belongs to.
	 */
	ariaLabel?: string
	isDisabled?: boolean
	/**
	 * Marks a field the form cannot be submitted without. Shows the asterisk
	 * always — that is information, not a complaint.
	 */
	isRequired?: boolean
	/**
	 * Whether an unset required field should render as an ERROR yet.
	 *
	 * Deliberately separate from `isRequired`, so a form does not open covered in
	 * red for fields the user has not had a chance to fill. Callers flip this on
	 * the first submit attempt — the first moment an empty value is genuinely a
	 * problem rather than a starting state.
	 */
	showError?: boolean
	/**
	 * Render a searchable combobox instead of a native select.
	 *
	 * For values that form a deep tree (geography: region → country → market), a
	 * flat indented `<select>` means scrolling hundreds of options. The combobox
	 * filters by label as you type and still indents by depth, so the hierarchy
	 * stays legible.
	 */
	isSearchable?: boolean
	/**
	 * A paged remote source has more options than are loaded. Renders a "Load
	 * more" row inside the searchable picker; meaningless without `isSearchable`,
	 * since a native `<select>` has nowhere to put it.
	 */
	hasMore?: boolean
	/** Fetch the next page — the caller appends to `options`. */
	onLoadMore?: () => void
	/** A page is in flight. */
	isLoadingMore?: boolean
}

/**
 * Labelled single-select.
 *
 * The design system's one picker that carries a visible label, hierarchy
 * indenting, a required marker and an error state together — every other
 * variant in the app had some subset, so each surface grew its own. Composes
 * the `Combobox` primitive when searchable and a native `<select>` otherwise;
 * both indent by `depth`.
 *
 * Presentational: options are resolved by the caller.
 */
export function SelectField({
	label,
	value,
	options,
	onChange,
	placeholder = 'all (untouched)',
	isClearable = true,
	ariaLabel,
	isDisabled = false,
	isRequired = false,
	showError = false,
	isSearchable = false,
	hasMore,
	onLoadMore,
	isLoadingMore,
}: SelectFieldProps) {
	const isUnsetRequired = isRequired && showError && value === ''
	const selectId = useId()

	if (isSearchable) {
		// Not wrapped in a <label>: the combobox owns its own input and labelling,
		// and nesting it would give the group a second, competing click target.
		return (
			<FieldShell label={label ?? ''} required={isRequired} className="text-ds-sm">
				<Combobox
					options={options}
					value={value || null}
					onChange={(next) => onChange(next ?? '')}
					placeholder={isRequired ? 'Select…' : placeholder}
					aria-label={ariaLabel ?? label}
					isDisabled={isDisabled}
					isInvalid={isUnsetRequired}
					isRequired={isRequired}
					isClearable={isClearable}
					hasMore={hasMore}
					onLoadMore={onLoadMore}
					isLoadingMore={isLoadingMore}
					className={cn(isUnsetRequired && 'border-ds-danger')}
				/>
			</FieldShell>
		)
	}

	return (
		<FieldShell label={label ?? ''} htmlFor={selectId} required={isRequired} className="text-ds-sm">
			<select
				id={selectId}
				className={cn(
					// `w-full` so the two branches of this component stand the same width. A
					// native select sizes to its CONTENT, so without it the field shrank to
					// the chosen option's label and drifted away from the fields above and
					// below it — visibly misaligned, and a smaller click target the longer
					// the list's labels were.
					'w-full rounded-ds-control border bg-ds-surface px-2 py-1.5 text-ds-sm text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
					// Only an UNSET required axis is flagged: once chosen it is as
					// settled as any other, and a permanent red ring would train the eye
					// to ignore it.
					isUnsetRequired ? 'border-ds-danger' : 'border-ds-border',
					isDisabled && 'opacity-60',
				)}
				value={value}
				disabled={isDisabled}
				aria-label={ariaLabel}
				aria-required={isRequired || undefined}
				aria-invalid={isUnsetRequired || undefined}
				onChange={(event) => onChange(event.target.value)}
			>
				{isClearable && <option value="">{isRequired ? 'Select…' : placeholder}</option>}
				{options.map((option) => (
					<option key={option.value} value={option.value}>
						{`${'  '.repeat(option.depth ?? 0)}${option.label}`}
					</option>
				))}
			</select>
		</FieldShell>
	)
}
